import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";

export const photoAccept = "image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.heic,.heif";
export type PhotoAsset = {
  id?: number;
  key?: string;
  position?: number;
  kind?: "seller" | "sale";
  is_cover?: boolean;
  can_edit?: boolean;
  archived_at?: string | null;
  url: string;
  thumb_url: string;
  download_url: string;
};
export type PhotoCollection = {
  revision: number;
  photos: (PhotoAsset & { id: number; position: number })[];
  seller_photo?: PhotoAsset | null;
  archived_photos?: PhotoAsset[];
  can_add_photos?: boolean;
  can_set_cover?: boolean;
  can_edit_photos?: boolean;
  can_edit_seller_photo?: boolean;
};
export const photoKey = (photo: PhotoAsset) => photo.key || `sale:${photo.id}`;
export function usePhotoCollection(productId: string) {
  return useQuery({
    queryKey: ["product-photos", productId],
    queryFn: () => api<PhotoCollection>(`product/${encodeURIComponent(productId)}/photos`),
    staleTime: 0,
  });
}
export function collectionPhotos(data?: PhotoCollection): PhotoAsset[] {
  if (!data) return [];
  return [...data.photos.map(photo => ({ ...photo, kind: "sale" as const })),
    ...(data.seller_photo ? [{ ...data.seller_photo, key: data.seller_photo.key || "seller", kind: "seller" as const }] : []),
    ...(data.archived_photos || [])];
}
export function archivedPhoto(data: PhotoCollection, photo: PhotoAsset) {
  return (data.archived_photos || []).some(item => photoKey(item) === photoKey(photo));
}
export function editablePhoto(data: PhotoCollection, photo: PhotoAsset) {
  return !archivedPhoto(data, photo) && (photo.kind === "seller" ? data.can_edit_seller_photo : data.can_edit_photos);
}

function validateFiles(files: File[]) {
  if (!files.length) throw Error("Выберите фотографию.");
  if (files.length > 30 || files.some(file => file.size > 40_000_000) || files.reduce((n, f) => n + f.size, 0) > 160 * 1024 * 1024)
    throw Error("До 30 снимков за раз, 40 МБ на снимок и 160 МБ на загрузку.");
}

export function usePhotoActions(productId: string, collection?: PhotoCollection) {
  const queryClient = useQueryClient();
  const running = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const root = `product/${encodeURIComponent(productId)}`;
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["product-photos", productId] }),
      queryClient.invalidateQueries({ queryKey: ["product", productId] }),
      queryClient.invalidateQueries({ queryKey: ["app-state"] }),
      queryClient.invalidateQueries({ queryKey: ["money"] }),
      queryClient.invalidateQueries({ queryKey: ["content"] }),
      queryClient.invalidateQueries({ queryKey: ["content-detail"] }),
    ]);
  };
  const perform = async (work: (revision: number) => Promise<unknown>, success: string) => {
    if (running.current) return false;
    running.current = true;
    setBusy(true); setError(""); setMessage("");
    try {
      if (!collection || !Number.isInteger(collection.revision)) throw Error("Обновите фотографии и повторите действие.");
      await work(collection.revision);
      await refresh();
      setMessage(success);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не удалось сохранить фотографии.");
      await refresh();
      return false;
    } finally { running.current = false; setBusy(false); }
  };
  const pathFor = (photo: PhotoAsset) => photo.kind === "seller" ? `${root}/seller-photo` : `${root}/photos/${photo.id}`;
  return {
    busy, error, message,
    clear: () => { setError(""); setMessage(""); },
    upload: (files: File[], seller = false) => perform(async revision => {
      validateFiles(files);
      if (seller && files.length !== 1) throw Error("Выберите один снимок до приезда.");
      const body = new FormData();
      body.append("revision", String(revision));
      files.forEach(file => body.append(seller ? "file" : "files", file));
      return api(`${root}/${seller ? "seller-photo" : "photos"}`, { method: "POST", body });
    }, "Фотографии сохранены."),
    replace: (photo: PhotoAsset, file: File) => perform(async revision => {
      validateFiles([file]);
      const body = new FormData(); body.append("revision", String(revision)); body.append("file", file);
      return api(pathFor(photo), { method: "POST", body });
    }, "Снимок заменён. Прежний доступен в истории фото."),
    remove: (photo: PhotoAsset) => perform(revision => api(pathFor(photo), {
      method: "POST", body: JSON.stringify({ action: "remove", revision }),
    }), "Снимок убран из текущего набора и сохранён в истории фото."),
    cover: (photo: PhotoAsset) => perform(revision => api(`${root}/photos/cover`, {
      method: "POST", body: JSON.stringify({ photo_id: photo.id, revision }),
    }), "Обложка изменена. Порядок фото для Telegram сохранён."),
  };
}
